import { useEffect, useMemo, useState } from 'react';
import {
  EXTRACTED_CSV_SEARCH_MAX_HITS,
  EXTRACTED_CSV_SEARCH_MIN_QUERY,
  type PricelistSearchHit,
  type PricelistTable,
} from '@erp/rpc';
import { rpcClient } from '@/api/rpc/client';
import {
  countHitsByTable,
  findHitsInTables,
  groupHitsByTable,
  type PricelistSearchGroup,
} from './pricelistSearch';

/** Typing pause before a search is worth a request. */
const DEBOUNCE_MS = 300;

/**
 * How long streamed hits are collected before they are rendered. Without it a
 * warm search that finds its cap in 40 ms would drive one render per hit.
 */
const FLUSH_MS = 40;

export type PricelistSearchStatus = 'idle' | 'searching' | 'settled' | 'error';

/**
 * Everything a view needs to render a search, whichever side it ran on. The two
 * hooks below both produce this, so the pricelist views don't care which of them
 * they were handed.
 */
export interface PricelistSearchView {
  /** True once the query is long enough to search — the "show results" switch. */
  searching: boolean;
  status: PricelistSearchStatus;
  /** Matching rows grouped by their table, in table order. */
  groups: PricelistSearchGroup[];
  /** Matches per table, for the tab badges. */
  hitCounts: Record<number, number>;
  totalHits: number;
  /** True when the hit cap stopped the search, so the count reads as "N+". */
  truncated: boolean;
}

const IDLE_VIEW: PricelistSearchView = {
  searching: false,
  status: 'idle',
  groups: [],
  hitCounts: {},
  totalHits: 0,
  truncated: false,
};

/** Whether a query is worth searching for at all. */
function isSearchable(query: string): boolean {
  return query.trim().length >= EXTRACTED_CSV_SEARCH_MIN_QUERY;
}

/**
 * Search a pricelist that is already in the browser.
 *
 * No request and no debounce: the rows are on hand, so the filter runs as the
 * user types, and it sees cells they have typed but not yet saved.
 */
export function useLoadedPricelistSearch({
  tables,
  query,
}: {
  tables: PricelistTable[];
  query: string;
}): PricelistSearchView {
  const trimmed = query.trim();
  const searching = isSearchable(trimmed);

  return useMemo(() => {
    if (!searching) return IDLE_VIEW;
    const { hits, truncated } = findHitsInTables(
      tables,
      trimmed,
      EXTRACTED_CSV_SEARCH_MAX_HITS,
    );
    return {
      searching: true,
      status: 'settled',
      groups: groupHitsByTable(hits, tables),
      hitCounts: countHitsByTable(hits),
      totalHits: hits.length,
      truncated,
    };
  }, [searching, tables, trimmed]);
}

/**
 * Search a pricelist too large to hold in the browser, rendering matches as they
 * arrive.
 *
 * The scan is server-side and streamed, so the first match appears long before
 * it finishes — on a cold pricelist that is the difference between a result at
 * ~200 ms and a blank panel until the whole blob has been downloaded. Retyping
 * aborts the request in flight, which stops the download too.
 */
export function useStoredPricelistSearch({
  csvDocumentId,
  query,
  tables,
  maxHits = EXTRACTED_CSV_SEARCH_MAX_HITS,
}: {
  csvDocumentId: string;
  query: string;
  /** The pricelist's tables, for grouping hits under their headers. */
  tables: { headers: string[] }[];
  maxHits?: number;
}): PricelistSearchView {
  const [state, setState] = useState<{
    status: PricelistSearchStatus;
    hits: PricelistSearchHit[];
    truncated: boolean;
  }>({ status: 'idle', hits: [], truncated: false });

  const trimmed = query.trim();
  const searching = isSearchable(trimmed);

  // Synchronizing with a streamed response — an external system, not derivable
  // state, which is what an effect is for.
  useEffect(() => {
    if (!searching) {
      setState({ status: 'idle', hits: [], truncated: false });
      return;
    }
    setState({ status: 'searching', hits: [], truncated: false });

    const controller = new AbortController();
    let pending: PricelistSearchHit[] = [];
    let flushTimer: ReturnType<typeof setTimeout> | null = null;

    // Aborting asks the request to stop; it does not stop this closure. Events
    // already in flight can still arrive, and every one of them would write into
    // the state the *next* query has just reset — appending the old query's hits
    // to the new query's results, or re-arming a flush after cleanup cleared it.
    // So nothing here touches state once the run is superseded.
    let cancelled = false;

    function flush(): void {
      flushTimer = null;
      if (cancelled || pending.length === 0) return;
      const batch = pending;
      pending = [];
      setState((prev) => ({ ...prev, hits: [...prev.hits, ...batch] }));
    }

    const debounce = setTimeout(() => {
      void (async () => {
        try {
          const events =
            await rpcClient.inventoryManagement.pricelist.searchExtractedCsv(
              { documentId: csvDocumentId, query: trimmed, maxHits },
              { signal: controller.signal },
            );

          let settled = false;
          for await (const event of events) {
            if (cancelled) return;
            if (event.type === 'hit') {
              pending.push(event.hit);
              flushTimer ??= setTimeout(flush, FLUSH_MS);
              continue;
            }
            if (flushTimer !== null) clearTimeout(flushTimer);
            flush();
            settled = true;
            setState((prev) => ({
              ...prev,
              status: 'settled',
              truncated: event.truncated,
            }));
          }
          // A stream that ends without its closing event — the server aborted,
          // or the connection dropped mid-scan — would otherwise leave this
          // parked in `searching` forever, showing a skeleton over hits it
          // already has. Settle on what arrived.
          if (!cancelled && !settled) {
            if (flushTimer !== null) clearTimeout(flushTimer);
            flush();
            setState((prev) => ({ ...prev, status: 'settled' }));
          }
        } catch {
          // An aborted request is this hook's own cleanup, not a failure.
          if (cancelled || controller.signal.aborted) return;
          setState((prev) => ({ ...prev, status: 'error' }));
        }
      })();
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(debounce);
      if (flushTimer !== null) clearTimeout(flushTimer);
      controller.abort();
    };
  }, [searching, csvDocumentId, trimmed, maxHits]);

  const { status, hits, truncated } = state;
  return useMemo(() => {
    if (status === 'idle') return IDLE_VIEW;
    return {
      searching: true,
      status,
      groups: groupHitsByTable(hits, tables),
      hitCounts: countHitsByTable(hits),
      totalHits: hits.length,
      truncated,
    };
  }, [status, hits, truncated, tables]);
}
